import React, { FC } from 'react';
import styled from 'styled-components';

import { mobile, tablet } from 'styles/breakpoints';
import { QuizAnswerButton } from 'components';
import { Question, QuestionOption } from 'types/quiz';
import { Typography } from 'utils/theme';

import Header from './Header';
import { HEADER_HEIGHT, HEADER_HEIGHT_MOBILE } from 'components/Header';
import { useSelector } from 'react-redux';
import { AppState } from 'state/types';

interface SingleProps {
  question: Question;
  selectedOptions: QuestionOption[];
  disabled: boolean;
  onSelect: (option: QuestionOption) => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: center;
  padding: 3.5rem 0 3rem;
  gap: 0.75rem;
  max-width: 28.75rem;
  width: 100%;
  @media ${tablet} {
    gap: 0.5rem;
    padding: 1.5rem 0;
  }
  @media ${mobile} {
    padding: 1.5rem 1rem;
  }
`;

const ButtonsContainer = styled.div`
  flex-direction: column;
  display: flex;
  width: 100%;
  button:nth-child(1n):not(:last-child) {
    margin-bottom: 0.5rem;
  }

  @media ${mobile} {
    justify-content: flex-end;
    width: 100%;
  }
`;

const zodiacInPlural = {
  aries: 'Aries',
  taurus: 'Tauruses',
  gemini: 'Geminis',
  cancer: 'Cancers',
  leo: 'Leos',
  virgo: 'Virgos',
  libra: 'Libras',
  scorpio: 'Scorpios',
  sagittarius: 'Sagittariuses',
  capricorn: 'Capricorns',
  aquarius: 'Aquariuses',
  pisces: 'Pisces',
};

const SingleByRelationshipStatus: FC<SingleProps> = ({
  question,
  selectedOptions,
  disabled,
  onSelect,
  ...props
}) => {
  const selected = (option: QuestionOption) =>
    selectedOptions.some(
      selectedOption => selectedOption.value === option.value,
    );

  const quizAnswers = useSelector((state: AppState) => state.user.quiz_answers);
  const relationshipStatus = quizAnswers?.relationship_status;

  const filteredKey = Object.keys(question).filter(key =>
    key.includes(relationshipStatus),
  );

  const filteredQuestion = question[filteredKey];
  const calculateAge = birthDateString => {
    const birthDate = new Date(birthDateString);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();

    if (
      monthDifference < 0 ||
      (monthDifference === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };

  const getAgeGroup = age => {
    switch (true) {
      case age >= 1 && age <= 19:
        return 'teens';

      default:
        return `${Math.floor(age / 10) * 10}s`;
    }
  };

  const age = calculateAge(quizAnswers?.birth_date);
  const ageGroup = getAgeGroup(age);

  const resolveTitle = (title: string) => {
    const capitalizeFirstLetter = (string: string) =>
      string.charAt(0).toUpperCase() + string.slice(1);

    if (quizAnswers?.gender === 'other') {
      const resolvedZodiac = title.replace(
        '${zodiac}',
        zodiacInPlural[quizAnswers?.zodiacSign],
      );
      return resolvedZodiac
        .replace('${gender}', '')
        .replace('${age}', ageGroup);
    }

    const capitalizedZodiac = capitalizeFirstLetter(quizAnswers?.zodiacSign);

    return title
      .replace('${zodiac}', capitalizedZodiac)
      .replace('${gender}', `${quizAnswers?.gender}s`)
      .replace('${age}', ageGroup);
  };

  const resolvedTitle = resolveTitle(filteredQuestion.label);

  return (
    <Container {...props}>
      <Header
        title={resolvedTitle}
        subtitle={(question?.optionsLabel || question.optionsLabelItalic) ?? ''}
        imageUrl={question.image}
        imageAlt={question.alt}
        optionsLabelFont={question.optionsLabelFont as Typography}
        imageHeightDesktop={question.imageHeightDesktop}
        imageHeightTablet={question.imageHeightTablet}
        imageHeightMobile={question.imageHeightMobile}
      />
      <ButtonsContainer>
        {filteredQuestion.options?.map((option, index) => (
          <QuizAnswerButton
            key={index}
            disabled={disabled}
            onClick={() => onSelect(option)}
            active={selected(option)}
            isRound={true}
            emoji={option.emoji}
            hideCheckIcon={true}
          >
            {option.label}
          </QuizAnswerButton>
        ))}
      </ButtonsContainer>
    </Container>
  );
};

export default SingleByRelationshipStatus;
